import React, { useCallback, useState } from 'react';

import { Tooltip } from '@primer/react';

import ConfirmationDialog from '../../../common/ConfirmationDialog/ConfirmationDialog';
import ContactSupport from '../../../common/Labels/ContactSupportLink';
import EmailInput from '../../../common/TextInputs/EmailInput/EmailInput';
import InfoCircleIcon from '../../../common/icons/InfoCircleIcon';
import Label from '../../../common/Labels/Label';
import PasswordInput from '../../../common/TextInputs/PasswordInput/PasswordInput';
import RadioGroup from '../../../common/RadioGroup/RadioGroup';

import { getThemeColor } from '../../../../utils/theme-utils';
import { isEmail } from '../../../common/TextInputs/EmailInput/email-utils';

const PASSWORD_GROUPS = [
  { id: 'temporary', label: 'Temporary' },
  { id: 'permanent', label: 'Permanent' },
];

const ROLE_GROUPS = [
  { id: 'user', label: 'User' },
  { id: 'admin', label: 'Admin' },
];

const CreateUserSection = ({
  error, name, password, isAdmin, isPasswordTemporary, setName, setPassword, setAdmin, setTemporaryPassword,
}) => {
  const emailError = !name?.length ? 'Invalid email' : null;
  const passwordError = !password?.length ? 'Invalid password' : null;

  const onRoleSelect = (selectedRole) => {
    setAdmin(selectedRole === 'admin');
  }
  const onPasswordGroupSelect = (selectedType) => {
    setTemporaryPassword(selectedType === 'temporary');
  }
  const onEntryValidation = entry => entry?.length;

  return (
    <div className="flex flex-col w-wide-modal">
      <div className="w-filter-modal flex flex-col">
        <div className="m-2 grid grid-cols-2">
          <div className="flex flex-col m-1">
            <Label bold className="m-1">User Email</Label>
            <EmailInput
              value={name}
              onChange={setName}
              onEntryValidation={onEntryValidation}
              errorMessage={emailError}
              block
            />
          </div>
          <div className="flex flex-col items-start m-1">
            <Label className="m-1" bold>Assign Type</Label>
            <RadioGroup
              id="role-selection-group"
              selectedItemId={isAdmin ? 'admin' : 'user'}
              items={ROLE_GROUPS}
              onSelect={onRoleSelect}
              renderAsRow
            />
          </div>
        </div>
        <div className="m-2 grid grid-cols-2">
          <div className="flex flex-col items-start justify-start m-1">
            <div className="flex items-center justify-start">
              <Label bold className="m-1">Password</Label>
              <Tooltip 
                aria-label="The password here needs to be sent to the user. If it is a temporary password, they will be prompted to change their password on next login."
                direction="ne"
                wrap
              >
                <InfoCircleIcon
                  size={4}
                  fill={getThemeColor('brand')}
                  color={getThemeColor('background')}
                  className="ml-1"
                />
              </Tooltip>
            </div>
            <PasswordInput
              value={password}
              onChange={setPassword}
              onEntryValidation={onEntryValidation}
              errorMessage={passwordError}
              block
            />
          </div>
          <div className="flex flex-col items-start m-1">
            <RadioGroup
              className="mt-8"
              id="password-mode-selection"
              selectedItemId={isPasswordTemporary ? 'temporary' : 'permanent'}
              items={PASSWORD_GROUPS}
              onSelect={onPasswordGroupSelect}
              renderAsRow
            />
          </div>
        </div>
      </div>
      {
        error && (
          <div className="p-2 h-full flex flex-col-reverse justify-start self-center">
            <Label color="error" size="sm">
              Error while creating the user. Please try again or <ContactSupport>contact support</ContactSupport> if the issue persists.
            </Label>
          </div>
        )
      }
    </div>
  )
}

export const CreateUserModal = ({
  error = null,
  onUserCreated = () => {},
  showDialog = false,
  onDialogToggle = () => {},
}) => {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [isAdmin, setAdmin] = useState(false);
  const [isTemporary, setTemporary] = useState(true);

  const disableConfirm = !name?.length || !password?.length || !isEmail(name);
  const onConfirm = useCallback(() => {
    onUserCreated(name, password, isAdmin, isTemporary);
  }, [isAdmin, isTemporary, name, onUserCreated, password])
  return (
    <ConfirmationDialog
      title="Create User"
      confirmButtonText="Create"
      showDialog={showDialog}
      onDialogToggle={onDialogToggle}
      modalAnimationKey="create-user-modal"
      onConfirm={onConfirm}
      disableConfirmationButton={disableConfirm}
    >
      <CreateUserSection
        error={error}
        name={name}
        password={password}
        isAdmin={isAdmin}
        isPasswordTemporary={isTemporary}
        setName={setName}
        setPassword={setPassword}
        setAdmin={setAdmin}
        setTemporaryPassword={setTemporary}
      />
    </ConfirmationDialog>
  )
};

export default CreateUserModal;
